import './app/styles/index.css';
import { registerSW } from 'virtual:pwa-register';
import { Router } from './app/router';
import { navBar } from './app/components/nav-bar';
import { showBanner } from './app/components/banner';
import { activePacks } from './core/region';
import { loadSelectedPacks } from './core/packs';
import { evictStalePacks } from './core/media/pack';
import { onWriteError } from './core/db';
import { loadIgnored } from './core/ignored';
import {
  SchemaVersionMismatchError,
  currentSession,
  setupStorage,
} from './core/storage';
import { el, ensureStyle } from './app/dom';
import { icon } from './app/components/icons';
import { syncThemeColor, watchSystemTheme } from './app/theme';
import { t } from './strings';

import { homeView } from './app/views/home';
import { libraryView } from './app/views/library';
import { statsView } from './app/views/stats';
import { youView } from './app/views/you';
import { quizSoundView } from './app/views/quiz-sound';
import { quizNamesView } from './app/views/quiz-names';
import { quizPhotoView } from './app/views/quiz-photo';
import { quizSoundMatchView } from './app/views/quiz-sound-match';
import { quizFieldView } from './app/views/quiz-field';
import { speciesDetailView } from './app/views/species-detail';
import { birdleView } from './app/views/birdle';
import { birdleCalendarView } from './app/views/birdle-calendar';
import { traitsView } from './app/views/traits';
import { compareView } from './app/views/compare';
import { creditsView } from './app/views/credits';
import { signIn } from './app/views/sign-in';

ensureStyle(
  'fatal-css',
  `
  .fatal { padding:40px var(--pad); display:flex; flex-direction:column; gap:14px;
    align-items:flex-start; }
  .fatal h1 { font-size:26px; margin:0; color:var(--danger);
    display:flex; align-items:center; gap:10px; }
  .fatal p { margin:0; }
`,
);

async function boot(): Promise<void> {
  const app = document.getElementById('app')!;

  // index.html has already set data-theme synchronously; this only picks up the
  // OS flipping mid-session, and re-reads --chrome now that tokens.css is live.
  watchSystemTheme();
  syncThemeColor();

  // Storage first: in PocketBase mode this is also the sign-in gate, and there
  // is nothing worth rendering until we know whose progress we are showing.
  try {
    await setupStorage((session) => signIn(app, session));
  } catch (err) {
    if (err instanceof SchemaVersionMismatchError) {
      return fatal(
        app,
        t('app.startup.versionMismatch'),
        t('app.startup.versionMismatchDetail', { message: err.message }),
      );
    }
    return fatal(app, t('app.startup.progressFailed'), (err as Error).message);
  }

  try {
    await loadSelectedPacks();
    // Which birds the learner has set aside. Read once, here, because every
    // quiz pool and every mastery denominator asks about it synchronously while
    // rendering — see core/ignored.ts.
    await loadIgnored();
    // Reclaim pack buckets from superseded versions, for each pack in force.
    // Fire-and-forget: a failure here costs disk, not correctness. Eviction only
    // ever touches buckets for the pack id it is given, so an unselected pack's
    // media survives untouched and re-selecting it costs no download.
    for (const manifest of activePacks()) {
      void evictStalePacks(manifest).catch(() => {});
    }
  } catch (err) {
    return fatal(app, t('app.startup.regionFailed'), (err as Error).message);
  }

  // A background write that never landed must be visible — progress lives on
  // the server, so a silent failure would quietly lose a study session.
  onWriteError((err) => {
    showBanner(t('app.banner.saveFailed', { message: err.message }), {
      label: t('common.reload'),
      run: () => location.reload(),
    });
  });

  const session = currentSession();
  if (session) session.onExpired = () => location.reload();

  app.replaceChildren();
  const outlet = el('div.outlet');
  app.append(outlet, navBar());

  const router = new Router(outlet);
  router
    .add('/home', homeView)
    .add('/library', libraryView)
    .add('/stats', statsView)
    .add('/you', youView)
    .add('/quiz/sound', quizSoundView)
    .add('/quiz/names', quizNamesView)
    .add('/quiz/photo', quizPhotoView)
    .add('/quiz/sound-match', quizSoundMatchView)
    .add('/quiz/field', quizFieldView)
    .add('/species/:code', speciesDetailView)
    .add('/compare/:left/:right', compareView)
    .add('/birdle', birdleView)
    .add('/birdle/calendar', birdleCalendarView)
    .add('/traits', traitsView)
    .add('/credits', creditsView)
    .start('/home');

  // Register the service worker (prompt-based updates).
  //
  // The prompt is a banner rather than confirm(): a native dialog steals focus
  // the moment the new worker installs, which is as likely to be halfway through
  // a Sound ID round as it is on the home screen — and dismissing it there costs
  // the answer in progress. The banner waits at the foot of the screen until the
  // learner is between questions, and Dismiss leaves the update for the next
  // launch, which is what "not now" should mean.
  const updateSW = registerSW({
    onNeedRefresh() {
      showBanner(
        t('app.banner.updateReady'),
        { label: t('common.reload'), run: () => void updateSW(true) },
        'info',
      );
    },
  });
}

function fatal(app: HTMLElement, title: string, detail: string): void {
  app.replaceChildren(
    el(
      'div.fatal',
      {},
      el('h1', {}, icon('alert', { size: 26 }), title),
      el('p.meta', {}, detail),
      el(
        'button.btn.btn-primary',
        { onclick: () => location.reload() },
        t('common.reload'),
      ),
    ),
  );
}

void boot();
